export default class DataAccess {
  constructor (securityManager, localStorage, externalStorage, message) {
    this.timeout = null
    this.waitTimeout = 3000
    this.externalStorageOnly = true
    this.securityManager = securityManager
    this.localStorage = localStorage
    this.externalStorage = externalStorage
    this.message = message

    this.closed = () => {
      this.message('down')
    }

    this.localStorage.onclose = this.closed
    this.localStorage.onblocked = this.closed
    this.localStorage.onversionchange = this.closed
    this.externalStorage.onclose = this.closed
  }

  init (username, password) {
    this.localStorage.open().then(() => {
      this.externalStorage.open(username, password).then(() => {
        this.message('up')
      }, () => {
        this.message('down')
      })
    }, () => {
      this.message('down')
    })
  }  

  setDataKey (passphrase) {
    this.securityManager.setDataKey(passphrase).then(() => {
      this.message('dataKeySet')
    }, (error) => {
      this.message('error', error.stack)
    })
  }

  setCredentials (username, password) {    
    this.externalStorage.setCredentials(username, password)
    this.message('credentialsSet')
  }

  isCurrentPassword (password) {
    return this.externalStorage.status(password)
  }

  externalChangePassword (password) {
    return this.externalStorage.changePassword(password)
  }

  localStatus () {
    return this.localStorage.status()
  }

  externalStatus () {
    return this.externalStorage.status()
  }

  read () {
    this.message('readPending')

    this.externalStorage.read().then((data) => {
      this.securityManager.decryptData(data).then((modelData) => {
        if (modelData) {
          this.localStorage.write(JSON.parse(modelData)).then(() => {
            this.message('read')
          }, (error) => {
            this.message('readFailed', error)
          })
        } else {
          this.message('read')
        }
      }, (error) => {
        this.message('readFailed', error)
      })
    }, (error) => {
      this.message('readFailed', error)
    })
  }

  write () {
    this.message('writePending')

    this.localStorage.read().then((modelData) => {
      this.securityManager.encryptData(JSON.stringify(modelData ? modelData : {})).then((data) => {
        this.externalStorage.write(data).then(() => {
          this.message('write')
        }, (error) => {
          this.timeout = setTimeout(this.autoWrite, this.waitTimeout)
          this.message('writeFailed', error ? error.stack : undefined)
        })
      }, (error) => {
        this.message('writeFailed', error ? error.stack : undefined)
      })
    }, (error) => {
      this.message('writeFailed', error ? error.stack : undefined)
    })
  }

  get (type, uuid) {
    if (this.externalStorageOnly) {
      return this.externalStorage.get({name: type, uncached: type === 'files'}, uuid)
    }

    return this.localStorage.get({name: type, uncached: type === 'files'}, uuid)
  }

  put (type, data) {
    clearTimeout(this.timeout)

    if (this.externalStorageOnly) {
      return this.externalStorage.put({name: type, uncached: type === 'files'}, data)
    }

    return this.localStorage.put({name: type, uncached: type === 'files'}, data)
  }

  delete (type, uuid) {
    clearTimeout(this.timeout)

    if (this.externalStorageOnly) {
      return this.externalStorage.delete({name: type, uncached: type === 'files'}, uuid)
    }

    return this.localStorage.delete({name: type, uncached: type === 'files'}, uuid)
  }

  autoWrite () {
    this.write()
  }

  resolveSingle (type, data, recursive) {
    return new Promise((resolve, reject) => {
      let model = window.PRIM.modelMapper.initToModel(type, data)

      switch (type) {
        default:
          resolve(model)
          break
        case 'positions':
          if (recursive) {
            let promises = 0
            if (model.values.person_id) {
              promises++
              window.PRIM.dataAccess.resolve('people', model.values.person_id, true).then((person) => {
                model.person = person
                promises--

                if (promises === 0) {
                  resolve(model)
                }
              }, () => {
                promises--

                if (promises === 0) {
                  reject()
                }
              })
            }
            if (model.values.location_id) {
              promises++
              window.PRIM.dataAccess.resolve('locations', model.values.location_id, true).then((location) => {
                model.location = location
                promises--

                if (promises === 0) {
                  resolve(model)
                }
              }, () => {
                promises--

                if (promises === 0) {
                  reject()
                }
              })
            }
          } else {
            resolve(model)
          }
          break
        case 'educations':
          if (model.values.location_id && recursive) {
            window.PRIM.dataAccess.resolve('locations', model.values.location_id, true).then((location) => {
              model.location = location
              resolve(model)
            }, reject)
          } else {
            resolve(model)
          }
          break
        case 'organisations':
          if (model.values.location_id && recursive) {
            window.PRIM.dataAccess.resolve('locations', model.values.location_id, false).then((location) => {
              model.location = location
              resolve(model)
            }, reject)
          } else {
            resolve(model)
          }
          break
        case 'locations':
          if (model.values.organisation_id && recursive) {
            window.PRIM.dataAccess.resolve('organisations', model.values.organisation_id, true).then((organisation) => {
              model.organisation = organisation
              resolve(model)
            }, reject)
          } else {
            resolve(model)
          }
          break
        case 'relationships':
          if (recursive) {
            let promises = 0
            if (model.values.person_id) {
              promises++
              window.PRIM.dataAccess.resolve('people', model.values.person_id, true).then((person) => {
                model.person = person
                promises--

                if (promises === 0) {
                  resolve(model)
                }
              }, () => {
                promises--

                if (promises === 0) {
                  reject()
                }
              })
            }

            if (model.values.target_id) {
              promises++
              window.PRIM.dataAccess.resolve('people', model.values.target_id, true).then((target) => {
                model.target = target
                promises--

                if (promises === 0) {
                  resolve(model)
                }
              }, () => {
                promises--

                if (promises === 0) {
                  reject()
                }
              })
            }
          } else {
            resolve(model)
          }
          break
      }
    })
  }

  resolve (type, id, recursive, indexkey) {
    return new Promise((resolve, reject) => {
      if (id && !indexkey) {
        window.PRIM.dataAccess.get(type, id).then((data) => {
          this.resolveSingle(type, data, recursive).then(resolve, reject)
        }, reject)
      } else {
        window.PRIM.dataAccess.get(type).then((data) => {
          let results

          if (indexkey) {
            results = data.filter((item) => {
              return item[indexkey] === id
            })
          } else {
            results = data
          }

          let promises = 0
          let models = []

          results.forEach((item) => {
            promises++
            this.resolveSingle(type, item, recursive).then((model) => {
              models.push(model)

              promises--
              if (promises === 0) {
                resolve(models)
              }
            }, () => {
              promises--
              if (promises === 0) {
                resolve(models)
              }
            })
          })
        })
      }
    })
  }
}



// WEBPACK FOOTER //
// ./js/lib/DataAccess.js